import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calculator, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import EntityPicker from "../components/EntityPicker";
import { toast } from "sonner";
import { adminApi } from "../api";

const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);

/**
 * AdminManualCommissionAdjustment
 *
 * Replica do "Adicionar acerto manual de comissões" do legacy back-office.
 * Insere uma linha em `comissao.Tinf_Comissao` com `ID_TipoComissao='AM'`.
 * Sem restrição: qualquer admin pode lançar valores positivos ou negativos
 * em `Valor Comissão` e/ou `Créditos`. Pelo menos um deles tem de ser != 0.
 */
export default function AdminManualCommissionAdjustment() {
  const [clientId, setClientId] = useState("");
  const [client, setClient] = useState(null);
  // Data do acerto: default = hoje (YYYY-MM-DD). Admin pode antedatar para
  // acertos retroactivos. Não pode ser futura (validado também no backend).
  const todayISO = new Date().toISOString().slice(0, 10);
  const [data, setData] = useState(todayISO);
  const [valorComissao, setValorComissao] = useState("");
  const [creditos, setCreditos] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const valorNum = Number(String(valorComissao).replace(",", ".")) || 0;
  const credNum = Number(String(creditos).replace(",", ".")) || 0;
  const dateValid = !!data && data <= todayISO;
  const canSubmit =
    !!clientId &&
    !submitting &&
    dateValid &&
    (Math.abs(valorNum) > 0 || Math.abs(credNum) > 0);

  const reset = () => {
    setValorComissao(""); setCreditos(""); setObservacoes("");
    setData(todayISO); setErr(""); setResult(null);
  };

  const doSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true); setErr(""); setResult(null);
    try {
      const r = await adminApi.opsManualCommissionAdjustment({
        id_cliente: Number(clientId),
        valor_comissao: valorNum || null,
        creditos: credNum || null,
        observacoes: observacoes.trim() || null,
        data,  // YYYY-MM-DD
      });
      setResult(r);
      toast.success("Acerto manual registado", { description: `${r.cliente_nome || `#${r.cliente_id}`}` });
    } catch (ex) {
      const msg = ex.message || "Erro";
      setErr(msg);
      toast.error(msg, { description: "Acerto manual de comissões" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-manual-commission-adjustment" className="max-w-3xl">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3">
        <ArrowLeft size={13} /> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><Calculator size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Acerto manual de comissões</h1>
          <p className="text-sm text-[#4B5563]">
            Insere uma linha em <code>Tinf_Comissao</code> (ID_TipoComissao=&apos;AM&apos;).
            Sem limite: valores positivos creditam, negativos debitam.
          </p>
        </div>
      </div>

      <form onSubmit={doSubmit} className="bg-white border border-[#E6E1D6] rounded-md p-5 space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">Cliente</label>
          <div className="mt-1.5">
            <EntityPicker
              entity="cliente"
              value={clientId}
              onChange={setClientId}
              onPick={(o) => setClient(o)}
              placeholder="Nome ou email do cliente..."
              testid="picker-client-manual-comm"
            />
          </div>
          {client && (
            <div className="mt-2 text-xs bg-[#FAF8F2] border border-[#E6E1D6] rounded p-2.5">
              <strong>{client.label}</strong>
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">
            Data do acerto <span className="text-[#978D78] normal-case tracking-normal">(default: hoje)</span>
          </label>
          <input
            type="date"
            value={data}
            max={todayISO}
            onChange={(e) => setData(e.target.value)}
            data-testid="adjustment-data"
            className={`mt-1.5 w-full sm:w-56 px-3 py-2 text-sm border rounded outline-none font-mono ${
              dateValid ? "border-[#E6E1D6] focus:border-[#0F1829]" : "border-red-400 bg-red-50"
            }`}
          />
          <p className="text-[10px] text-[#6B7280] mt-1">
            Para acertos retroactivos. Não pode ser futura.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">
              Valor Comissão (€)
            </label>
            <input
              type="number"
              step="0.01"
              value={valorComissao}
              onChange={(e) => setValorComissao(e.target.value)}
              data-testid="adjustment-valor"
              placeholder="Ex: 100.00 ou -25.50"
              className="mt-1.5 w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829] font-mono"
            />
            <p className="text-[10px] text-[#6B7280] mt-1">
              Soma directa em comissões a receber. Negativo = débito.
            </p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">
              Créditos
            </label>
            <input
              type="number"
              step="0.01"
              value={creditos}
              onChange={(e) => setCreditos(e.target.value)}
              data-testid="adjustment-creditos"
              placeholder="Ex: 50.00 ou -10.00"
              className="mt-1.5 w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829] font-mono"
            />
            <p className="text-[10px] text-[#6B7280] mt-1">
              Soma na carteira de créditos do cliente. Negativo = débito.
            </p>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">
            Observações <span className="text-[#978D78] normal-case tracking-normal">(opcional · 250 car.)</span>
          </label>
          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={2}
            maxLength={250}
            placeholder="Ex: Consultoria coloração pessoal - Maria Paula"
            data-testid="adjustment-observacoes"
            className="mt-1.5 w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829]"
          />
        </div>

        {err && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{err}</span>
          </div>
        )}

        {result && (
          <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 rounded flex items-start gap-2" data-testid="adjustment-success">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
            <div>
              Acerto registado em {result.cliente_nome || `#${result.cliente_id}`}:
              {result.valor_comissao != null && <> Comissão {fmt(result.valor_comissao)}</>}
              {result.creditos != null && <> · Créditos {fmt(result.creditos)}</>}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            onClick={reset}
            disabled={submitting}
            className="px-4 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#F5F2EB]"
          >
            Limpar
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="adjustment-submit"
            className="px-4 py-2 bg-[#0F1829] text-white text-sm rounded disabled:opacity-40 hover:bg-black transition inline-flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Registar acerto
          </button>
        </div>
      </form>
    </div>
  );
}
